import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { FC } from "react";
import { TTiOverviewDTO, T2dCoordinate, TPolarCoordinate, TTechnology, TConcept, TConceptTechnologyListDTO } from "Types";
import * as d3 from "d3";
import { HardcodedTrlRadarSvgsSingleton } from "./HardcodedTrlRadarSvgs";
import { CaseActionTypeEnum, CasePageEnum, VoteTypeEnum } from "Enums";
// styles
import styles from "./trlRadar.module.scss";
import { CaseContext } from "Stores";
import { ICaseSelectedConcept, ICaseSelectedTechnology, ICaseSetSelectedEntity } from "Interfaces";
import { ConceptConstants } from "Constants";

type TTrlRadarProps = {
    tiOverviewDto: TTiOverviewDTO | null,
    voteType: VoteTypeEnum
}

export const TrlRader: FC<TTrlRadarProps> = ({ tiOverviewDto }: TTrlRadarProps) => {

    const ringMargin = 6;

    const { caseState, caseDispatch } = useContext(CaseContext);

    useEffect(() => {
        if(!caseState.conceptTechnologyList) return;
        if(!caseState.conceptTechnologyList.publishedPhaseOptions.doShowRadar) {
            caseDispatch({
                type: CaseActionTypeEnum.SetCaseSelectedEntity, selectedEntityId: -1,
                selectedEntityName: CasePageEnum.Overview, selectedEntityNumbering: ""
            } as ICaseSetSelectedEntity);
        }
    }, [caseState.conceptTechnologyList, caseDispatch]);

    const rings = useMemo(() => {
        return [
            { radius: 40, color: "#FFFFFF" },
            { radius: 120, color: "#87D9F9" },
            { radius: 122, color: "#FFFFFF" },
            { radius: 200, color: "#9EE0FA" },
            { radius: 202, color: "#FFFFFF" },
            { radius: 280, color: "#B9E9FB" },
            { radius: 282, color: "#FFFFFF" },
            { radius: 360, color: "#D9F3FD" }
        ]
    }, []);

    const svgRef = React.useRef(null);

    function cartesian(polar: TPolarCoordinate): T2dCoordinate {
        return {
            x: (polar.r * Math.cos(polar.t)),
            y: (polar.r * Math.sin(polar.t))
        };
    }

    function textAlignToCssTextAlign(textAlign: string): string {
        if (textAlign === "middle") {
            return "center";
        } else if (textAlign === "begin") {
            return "left";
        } else if (textAlign === "end") {
            return "right";
        }
        return "center";
    }

    function justifyContentToCssTextAlign(textAlign: string): string {
        if (textAlign === "middle") {
            return "center";
        } else if (textAlign === "begin") {
            return "flex-start";
        } else if (textAlign === "end") {
            return "flex-end";
        }
        return "center";
    }

    function determineLineEndingX(textAlign: string, lineEndCoordinate: T2dCoordinate): number {
        if (textAlign === "end") {
            return lineEndCoordinate.x - 700;
        } else if (textAlign === "begin") {
            return lineEndCoordinate.x;
        } else {
            return lineEndCoordinate.x - 350;
        }
    }

    function determineLineEndingY(textAlign: string, lineEndCoordinate: T2dCoordinate): number {
        if (textAlign === "middle") {
            return lineEndCoordinate.y - 50;
        } else {
            return lineEndCoordinate.y - 25;
        }
    }

    function determineTechnologyLineEndingX(textAlign: string, newCoordinate: T2dCoordinate): number {
        if (textAlign === "end") {
            return newCoordinate.x - 700;
        } else {
            return newCoordinate.x;
        }

    }

    function countAllTechnologies(conceptTechnologyList: TConceptTechnologyListDTO): number {
        let countedTechnologies = conceptTechnologyList.additionalTechnologies.length;

        for (let i = 0; i < conceptTechnologyList.concepts.length; i++) {
            const currentConcept = conceptTechnologyList.concepts[i];
            countedTechnologies += currentConcept.technologies.length;
        }

        return countedTechnologies;
    }

    const gotoConcept = useCallback((concept: TConcept): void => {
        caseDispatch({
            type: CaseActionTypeEnum.SetCaseSelectedConcept, selectedConceptId: concept.id,
            selectedEntityName: concept.name, selectedEntityNumbering: concept.numbering
        } as ICaseSelectedConcept);
    }, [caseDispatch]);

    const gotoTechnology = useCallback((technology: TTechnology): void => {
        caseDispatch({
            type: CaseActionTypeEnum.SetCaseSelectedTechnology,
            selectedTechnologyId: technology.id,
            selectedOriginalTechnologyId: technology.originalTechnologyId,
            selectedEntityName: technology.name,
            selectedEntityNumbering: technology.numbering
        } as ICaseSelectedTechnology);
    }, [caseDispatch]);

    // TODO: fix this any
    const onTechnologyMouseOver = (technologyElementChildren: NodeListOf<any>): void => {
        for (let i = 0; i < technologyElementChildren.length; i++) {
            const element = technologyElementChildren[i];
            if(element.tagName === "foreignObject") {
                d3.select(element).style("color", "#00adef");  
            } else if(element.tagName === "ellipse") {
                d3.select(element).style("fill", "#ffffff").style("stroke", "#00adef").style("stroke-width", "2"); 
            } else {
                d3.select(element).style("fill", "#00adef").style("stroke", "#00adef"); 
            }
        }  
    };

    // TODO: fix this any
    const onTechnologyMouseOut = (technologyElementChildren: NodeListOf<any>): void => {
        for (let i = 0; i < technologyElementChildren.length; i++) {
            const element = technologyElementChildren[i];
            if(element.tagName === "foreignObject") {
                d3.select(element).style("color", "#252525"); 
            } else if (element.tagName === "ellipse") {
                d3.select(element).style("fill", "#7FBCDE").style("stroke", "#003171").style("stroke-width", "1"); 
            } else {
                d3.select(element).style("fill", "#003171").style("stroke", "#003171"); 
            }
        }  
    };

    const createDefaultRadarLayout = useCallback((grid: d3.Selection<SVGGElement, unknown, null, undefined>) => {
        const defs = grid.append("defs");
        const filter = defs.append("filter")
            .attr("x", 0)
            .attr("y", 0)
            .attr("width", 1)
            .attr("height", 1)
            .attr("id", "solid");
        filter.append("feFlood")
            .attr("flood-color", "rgb(0, 0, 0, 0.8)");
        filter.append("feComposite")
            .attr("in", "SourceGraphic");

        for (var i = rings.length; i > 0; i--) {
            grid.append("circle")
                .attr("cx", 0)
                .attr("cy", 0)
                .attr("r", rings[i - 1].radius)
                .style("fill", rings[i - 1].color);
        }

        grid.append("line")
            .attr("x1", rings[0].radius - 10).attr("y1", 0)
            .attr("x2", rings[rings.length - 1]["radius"] + 10).attr("y2", 0)
            .style("stroke", "white")
            .style("fill", "white")
            .style("stroke-width", 20);

        grid.append("svg:foreignObject")
            .attr("x", 70).attr("y", -8)
            .attr("width", "30px").attr("height", "16px")
            .attr("font-family", "FontAwesome")
            .attr("font-size", "16px")
            .style("color", "black")
            .html(HardcodedTrlRadarSvgsSingleton.getShoppingCartSvg());
        grid.append("svg:foreignObject")
            .attr("x", 145).attr("y", -8)
            .attr("width", "30px").attr("height", "16px")
            .attr("font-family", "FontAwesome")
            .attr("font-size", "16px")
            .style("color", "black")
            .html(HardcodedTrlRadarSvgsSingleton.getCogsSvg());
        grid.append("svg:foreignObject")
            .attr("x", 230).attr("y", -8)
            .attr("width", "30px").attr("height", "16px")
            .attr("font-family", "FontAwesome")
            .attr("font-size", "16px")
            .style("color", "black")
            .html(HardcodedTrlRadarSvgsSingleton.getFlaskSvg());
        grid.append("svg:foreignObject")
            .attr("x", 310).attr("y", -8)
            .attr("width", "30px").attr("height", "16px")
            .attr("font-size", "16px")
            .style("color", "black")
            .html(HardcodedTrlRadarSvgsSingleton.getAtomSvg());
    }, [rings]);

    const drawTechnology = useCallback((grid: d3.Selection<SVGGElement, unknown, null, undefined>, technology: TTechnology, conceptIndex: number, 
            technologyIndex: number, part: number, cumulative: number, totalEntityCount: number) => {
        let textAlign = "middle";
        let textCumulative = cumulative % 1.0;
        let textMargin = 30;

        let lineBeginPolarCoordinate: TPolarCoordinate = { r: rings[0].radius, t: (cumulative) * 2 * Math.PI };
        let lineBeginCoordinate: T2dCoordinate = cartesian(lineBeginPolarCoordinate);
        let lineEndPolarCoordinate: TPolarCoordinate = { r: (rings[rings.length - 1]["radius"]) + textMargin, t: cumulative * 2 * Math.PI };
        let lineEndCoordinate: T2dCoordinate = cartesian(lineEndPolarCoordinate);
        let newPolarCoordinate: TPolarCoordinate;
        let newCoordinate: T2dCoordinate;

        textCumulative = cumulative % 1.0;
        if (conceptIndex === 0 && technologyIndex === 0) {
            textAlign = "begin";
        } else if (textCumulative >= 0.74 && textCumulative <= 0.76) {
            textAlign = "middle";
        } else if (textCumulative >= 0.24 && textCumulative <= 0.26) {
            textAlign = "end";
        } else if (textCumulative >= 0.26 && textCumulative <= 0.76) {
            textAlign = "end";
        } else {
            textAlign = "begin";
        }

        if (textCumulative > 0.95 || textCumulative < 0.05) {
            cumulative = 1.05;
        }
        textCumulative = cumulative % 1.0;

        let circleCoordinate: T2dCoordinate | undefined = undefined;
        let rangeLength = 0;
        let trlRangeCenter = 0;
        let trlPart = 0.5;
        let ellipseCoordinate = null;

        const ringOneEnd = (rings[0]["radius"]) + ringMargin; // Plus a margin
        const ringLastEnd = (rings[rings.length - 1]["radius"]) - 2 * ringMargin; // Plus a margin

        const circle = grid.append("g").attr("class", "circle");

        if (technology.trl && technology.trl.minimumTrl && technology.trl.maximumTrl) {
            const max_trl = Math.max(technology.trl.minimumTrl, technology.trl.maximumTrl);
            const min_trl = Math.min(technology.trl.minimumTrl, technology.trl.maximumTrl);
            rangeLength = ((max_trl - min_trl) / 9.0);
            rangeLength = (ringLastEnd * rangeLength);
            trlRangeCenter = 1 - ((max_trl + min_trl) / 18.0);
            const ellipseWidth = 16 - ((8 / 40) * totalEntityCount);

            ellipseCoordinate = { r: rings[0]["radius"] + (rings[rings.length - 1]["radius"] * trlRangeCenter), t: cumulative * 2 * Math.PI }
            ellipseCoordinate = cartesian(ellipseCoordinate);

            circle.append("ellipse")
                .on("click", function () {
                    gotoTechnology(technology);
                })
                .on("mouseover", function () {
                    if(this.parentElement) onTechnologyMouseOver(this.parentElement.childNodes);
                })
                .on("mouseout", function () {
                    if(this.parentElement) onTechnologyMouseOut(this.parentElement.childNodes);
                })
                .attr("cx", ellipseCoordinate.x).attr("cy", ellipseCoordinate.y)
                .attr("rx", ellipseWidth)
                .attr("ry", rangeLength / 2.0)
                .attr("transform", "rotate(" + (-90 + (360 * cumulative)) + "," + ellipseCoordinate.x + "," + ellipseCoordinate.y + ")")
                .style("stroke", "#003171")
                .attr("stroke-width", 1)
                .style("fill", "#7FBCDE");
        } else if (!technology.trl?.technologyReadinessLevel && !technology.trl?.minimumTrl && !technology.trl?.maximumTrl) {
            trlPart = 1 - (0.5 / 9.0);
            const circlePolarCoordinate: TPolarCoordinate = { r: ringOneEnd + (ringLastEnd * trlPart), t: cumulative * 2 * Math.PI };
            circleCoordinate = cartesian(circlePolarCoordinate);
        }

        if (technology.trl && technology.trl.technologyReadinessLevel) {
            trlPart = 1 - (technology.trl.technologyReadinessLevel / 9.0);
            const circlePolarCoordinate: TPolarCoordinate = { r: ringOneEnd + (ringLastEnd * trlPart), t: cumulative * 2 * Math.PI }
            circleCoordinate = cartesian(circlePolarCoordinate);
        }

        textCumulative = cumulative % 1.0;
        if (textCumulative > 0.05 && textCumulative < 0.45) {
            let extraMargin = Math.abs(textCumulative - 0.25);
            extraMargin = 0.20 - (extraMargin);
            textMargin = 30 + (Math.pow(extraMargin, 3) * 15000);
        } else if (textCumulative > 0.55 && textCumulative < 0.95) {
            let extraMargin = Math.abs(textCumulative - 0.75);
            extraMargin = 0.20 - (extraMargin);
            textMargin = 30 + (Math.pow(extraMargin, 3) * 15000);
        } else {
            textMargin = 30;
        }


        if (technology.trl && technology.trl.minimumTrl && technology.trl.maximumTrl) {
            lineBeginPolarCoordinate = { r: rings[0]["radius"] + (rings[rings.length - 1]["radius"] * trlRangeCenter) + (rangeLength / 2.0), t: cumulative * 2 * Math.PI }
            lineBeginCoordinate = cartesian(lineBeginPolarCoordinate);
            lineEndPolarCoordinate = { r: rings[rings.length - 1]["radius"] + textMargin, t: cumulative * 2 * Math.PI }
            lineEndCoordinate = cartesian(lineEndPolarCoordinate);

            circle.append("line")
                .on("click", function () {
                    gotoTechnology(technology);
                })
                .on("mouseover", function () {
                    if(this.parentElement) onTechnologyMouseOver(this.parentElement.childNodes);
                })
                .on("mouseout", function () {
                    if(this.parentElement) onTechnologyMouseOut(this.parentElement.childNodes);
                })
                .attr("x1", lineBeginCoordinate.x)
                .attr("y1", lineBeginCoordinate.y)
                .attr("x2", lineEndCoordinate.x)
                .attr("y2", lineEndCoordinate.y)
                .attr("stroke-width", 2)
                .style("stroke", "#003171");

            if (circleCoordinate) {
                circle.append("circle")
                    .on("click", function () {
                        caseDispatch({
                            type: CaseActionTypeEnum.SetCaseSelectedTechnology,
                            selectedTechnologyId: technology.id,
                            selectedOriginalTechnologyId: technology.originalTechnologyId,
                            selectedEntityName: technology.name,
                            selectedEntityNumbering: technology.numbering
                        } as ICaseSelectedTechnology);
                    })
                    .on("mouseover", function () {
                        if(this.parentElement) onTechnologyMouseOver(this.parentElement.childNodes);
                    })
                    .on("mouseout", function () {
                        if(this.parentElement) onTechnologyMouseOut(this.parentElement.childNodes);
                    })
                    .attr("cx", circleCoordinate.x).attr("cy", circleCoordinate.y)
                    .attr("r", 6)
                    .style("fill", "#003171");
            }
        } else {
            lineBeginPolarCoordinate = { r: ringOneEnd + (ringLastEnd * trlPart), t: cumulative * 2 * Math.PI }
            lineBeginCoordinate = cartesian(lineBeginPolarCoordinate);
            lineEndPolarCoordinate = { r: rings[rings.length - 1]["radius"] + textMargin, t: cumulative * 2 * Math.PI }
            lineEndCoordinate = cartesian(lineEndPolarCoordinate);

            circle.append("line")
                .on("click", function () {
                    gotoTechnology(technology);
                })
                .on("mouseover", function () {
                    if(this.parentElement) onTechnologyMouseOver(this.parentElement.childNodes);
                })
                .on("mouseout", function () {
                    if(this.parentElement) onTechnologyMouseOut(this.parentElement.childNodes);
                })
                .attr("x1", lineBeginCoordinate.x)
                .attr("y1", lineBeginCoordinate.y)
                .attr("x2", lineEndCoordinate.x)
                .attr("y2", lineEndCoordinate.y)
                .attr("stroke-width", 2)
                .style("stroke", "#003171");

            if (circleCoordinate) {
                circle.append("circle")
                    .on("click", function() { 
                        caseDispatch({
                            type: CaseActionTypeEnum.SetCaseSelectedTechnology,
                            selectedTechnologyId: technology.id,
                            selectedOriginalTechnologyId: technology.originalTechnologyId,
                            selectedEntityName: technology.name,
                            selectedEntityNumbering: technology.numbering
                        } as ICaseSelectedTechnology);
                    })
                    .on("mouseover", function () {
                        if(this.parentElement) onTechnologyMouseOver(this.parentElement.childNodes);
                    })
                    .on("mouseout", function () {
                        if(this.parentElement) onTechnologyMouseOut(this.parentElement.childNodes);
                    })
                    .attr("cx", circleCoordinate.x).attr("cy", circleCoordinate.y)
                    .attr("r", 6)
                    .style("fill", "#003171");
            }
        }

        newPolarCoordinate = { r: rings[rings.length - 1]["radius"] + textMargin + 25, t: cumulative * 2 * Math.PI }
        newCoordinate = cartesian(newPolarCoordinate);

        circle.append("foreignObject")
            .attr("height", "64")
            .attr("width", "700")
            .on("click", function () {
                gotoTechnology(technology);
            })
            .on("mouseover", function () {
                if(this.parentElement) onTechnologyMouseOver(this.parentElement.childNodes);
            })
            .on("mouseout", function () {
                if(this.parentElement) onTechnologyMouseOut(this.parentElement.childNodes); 
            })
            .attr("x", determineTechnologyLineEndingX(textAlign, newCoordinate))
            .attr("y", (newCoordinate.y - 25))
            .style("color", "#252525")
            .append("xhtml:div")
            .style("text-align", textAlignToCssTextAlign(textAlign))
            .style("display", "flex")
            .style("height", "100%")
            .style("width", "100%")
            .style("font-size", "28px")
            .style("justify-content", justifyContentToCssTextAlign(textAlign))
            .append("xhtml:div")
            .style("display", "table-cell")
            .style("vertical-align", "middle")
            .style("line-height", "32px")
            .style("cursor", "default")
            .text(technology.name)
            .attr("data-original", technology.name)
            .attr("data-id", technology.id);

        return cumulative + part;
    }, [caseDispatch, gotoTechnology, rings]);

    const drawConceptAndChildren = useCallback((grid: d3.Selection<SVGGElement, unknown, null, undefined>, concept: TConcept, conceptIndex: number,
            part: number, cumulative: number, totalEntityCount: number) => {
        let textAlign = "middle";
        let textCumulative = cumulative % 1.0;
        let textMargin = 30;
        let extraMargin = -1;

        textCumulative = cumulative % 1.0;
        if (textCumulative > 0.95 || textCumulative < 0.05) {
            cumulative = 1.05;
        }
        textCumulative = cumulative % 1.0;

        if (conceptIndex === 0) {
            textAlign = "middle";
        } else if (textCumulative >= 0.74 && textCumulative <= 0.76) {
            textAlign = "middle";
        } else if (textCumulative >= 0.24 && textCumulative <= 0.26) {
            textAlign = "end";
        } else if (textCumulative >= 0.26 && textCumulative <= 0.76) {
            textAlign = "end";
        } else {
            textAlign = "begin";
        }

        if (textCumulative === 0.75) {
            textMargin = 140;
        } else if (textCumulative > 0.05 && textCumulative < 0.45) {
            extraMargin = Math.abs(textCumulative - 0.25);
            extraMargin = 0.20 - (extraMargin);
            textMargin = 30 + (Math.pow(extraMargin, 3) * 15000);
        } else if (textCumulative > 0.55 && textCumulative < 0.95) {
            extraMargin = Math.abs(textCumulative - 0.75);
            extraMargin = 0.20 - (extraMargin);
            textMargin = 30 + (Math.pow(extraMargin, 3) * 15000);
        } else {
            textMargin = 30;
        }

        const lineBeginPolarCoordinate: TPolarCoordinate = { r: rings[0].radius, t: (cumulative) * 2 * Math.PI };
        const lineBeginCoordinate: T2dCoordinate = cartesian(lineBeginPolarCoordinate);
        let lineEndPolarCoordinate: TPolarCoordinate = { r: (rings[rings.length - 1]["radius"]) + textMargin, t: cumulative * 2 * Math.PI };
        let lineEndCoordinate: T2dCoordinate = cartesian(lineEndPolarCoordinate);

        // Draw concept lines
        grid.append("line")
            .attr("x1", lineBeginCoordinate.x).attr("y1", lineBeginCoordinate.y)
            .attr("x2", lineEndCoordinate.x).attr("y2", lineEndCoordinate.y)
            .style("stroke", "#00ADEF")
            .style("fill", "#00ADEF")
            .style("stroke-width", 3);

        lineEndPolarCoordinate = { r: (rings[rings.length - 1]["radius"]) + textMargin + 25, t: cumulative * 2 * Math.PI };
        lineEndCoordinate = cartesian(lineEndPolarCoordinate);
        const dataFiltered = concept.name.replace("<", "");

        grid.append("foreignObject")
            .attr("class", "circle")
            .attr("height", "64")
            .attr("width", "700")
            .on("click", function () {
                gotoConcept(concept);
            })
            .on("mouseover", function () { d3.select(this).style("color", "#00adef"); })
            .on("mouseout", function () { d3.select(this).style("color", "#000000"); })
            .attr("x", determineLineEndingX(textAlign, lineEndCoordinate))
            .attr("y", determineLineEndingY(textAlign, lineEndCoordinate))
            .style("color", "#252525")
            .append("xhtml:div").style("height", "100%").style("width", "100%")
            .style("text-align", textAlignToCssTextAlign(textAlign))
            .style("font-size", "28px")
            .style("font-weight", "500")
            .style("display", "table")
            .append("xhtml:div")
            .style("display", "table-cell")
            .style("vertical-align", textAlign === "middle" ? "bottom" : "middle")
            .style("line-height", "32px")
            .style("cursor", "default")
            .text(concept.name)
            .attr("data-original", dataFiltered);

        cumulative += (1 * part);
        for (let j = 0; j < concept.technologies.length; j++) {
            const technology = concept.technologies[j];

            cumulative = drawTechnology(grid, technology, conceptIndex, j, part, cumulative, totalEntityCount);
        }

        return cumulative;
    }, [drawTechnology, gotoConcept, rings]);

    React.useEffect(() => {

        const svgElement = d3.select(svgRef.current)
            .attr("viewBox", "-1115 -575 2200 1125")
            .classed("svg-content-responsive", true);
        const grid = svgElement.append("g");
        createDefaultRadarLayout(grid);

        if (!tiOverviewDto || !caseState.conceptTechnologyList) return;

        let cumulative = 0.75;

        const totalTechnologies = countAllTechnologies(caseState.conceptTechnologyList);
        const additionalConceptAdditionNumber = caseState.conceptTechnologyList.additionalTechnologies.length > 0 ? 1 : 0;
        const totalEntityCount = totalTechnologies + caseState.conceptTechnologyList.concepts.length + additionalConceptAdditionNumber;
        const part = 0.9 / (totalTechnologies + caseState.conceptTechnologyList.concepts.length + additionalConceptAdditionNumber);

        for (let i = 0; i < caseState.conceptTechnologyList.concepts.length; i++) {
            const concept = caseState.conceptTechnologyList.concepts[i];

            cumulative = drawConceptAndChildren(grid, concept, i, part, cumulative, totalEntityCount);
        }

        if(caseState.conceptTechnologyList.additionalTechnologies.length > 0) {
            drawConceptAndChildren(grid, { id: ConceptConstants.FIXED_ADDITIONAL_CONCEPT_ID, name: ConceptConstants.FIXED_ADDITIONAL_CONCEPT_NAME, 
                technologies: caseState.conceptTechnologyList.additionalTechnologies } as TConcept,
                caseState.conceptTechnologyList.concepts.length, part, cumulative, totalEntityCount);
        }

        // TODO: add stars/votes
    }, [caseDispatch, gotoTechnology, gotoConcept, drawConceptAndChildren, tiOverviewDto, caseState.conceptTechnologyList, rings, createDefaultRadarLayout]);

    return (
        <>
            <div className={styles.headerContainer}>
                <h2>Maturity radar</h2>
            </div>
            <div className={[styles.radarContainer, styles.scrollbarCustomThin].join(" ")}>
                <svg ref={svgRef} />
            </div>
        </>
    );

}